import { TOKENS } from "@/lib/tokens";

interface WordmarkProps {
  size?: "sm" | "md" | "lg";
  /**
   * Color treatment for the brand mark.
   *  - "ink" (default): solid TOKENS.text (use on light cards / surfaces)
   *  - "onDepth": white wordmark with TOKENS.accent on "Talenta" (use on dark
   *    depth bands like AppShell header)
   */
  variant?: "ink" | "onDepth";
  /**
   * When true, stack "Finmark" / "Talenta" vertically on viewports < sm
   * (640px) and revert to single-line on sm+. Used in the AppShell header
   * to free horizontal space for menu icons on narrow mobile viewports
   * where the hamburger menu would otherwise be clipped off-screen.
   *
   * LoginCard and any free-standing brand surfaces keep the default
   * single-line rendering for visual prominence.
   */
  stackOnMobile?: boolean;
}

const SIZES = {
  sm: { fontSize: "1rem", letterSpacing: "0.02em", height: "32px" },
  md: { fontSize: "1.25rem", letterSpacing: "0.03em", height: "44px" },
  lg: { fontSize: "1.5rem", letterSpacing: "0.04em", height: "56px" },
} as const;

export function Wordmark({
  size = "md",
  variant = "ink",
  stackOnMobile = false,
}: WordmarkProps) {
  const s = SIZES[size];
  const baseColor = variant === "onDepth" ? TOKENS.depthFg : TOKENS.text;
  const accentColor = TOKENS.primary;

  if (stackOnMobile) {
    // Mobile: flex-col, no fixed height (two stacked lines fit naturally
    // within the 56px header at lineHeight 1.1). sm+: revert to inline.
    return (
      <span
        className="font-semibold flex flex-col items-start sm:inline-flex sm:flex-row sm:items-center"
        style={{
          fontSize: s.fontSize,
          letterSpacing: s.letterSpacing,
          color: baseColor,
          lineHeight: 1.1,
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        }}
        aria-label="Finmark Talenta"
      >
        <span>Finmark</span>
        <span style={{ color: accentColor }} className="sm:ml-1.5">
          Talenta
        </span>
      </span>
    );
  }

  // Default single-line. Non-breaking space (U+00A0) inside the first span
  // survives whitespace collapse that inline-flex applies between flex-item
  // children. Regular " " gets stripped at flex boundaries; " " doesn't.
  return (
    <span
      className="font-semibold inline-flex items-center"
      style={{
        fontSize: s.fontSize,
        letterSpacing: s.letterSpacing,
        height: s.height,
        color: baseColor,
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        whiteSpace: "nowrap",
      }}
      aria-label="Finmark Talenta"
    >
      <span>{"Finmark "}</span>
      <span style={{ color: accentColor }}>Talenta</span>
    </span>
  );
}
